/**
 * Utility functions for making charts responsive across different viewport sizes
 */

export interface ChartSizeConfig {
  isVerySmall: boolean;
  isSmall: boolean;
  isMedium: boolean;
  containerWidth: number;
  containerHeight: number;
}

export interface ChartResponsiveConfig {
  showTitle: boolean;
  showDescription: boolean;
  titleClass: string;
  titleMargin: string;
  axisFontSize: number;
  legendFontSize: number;
  margin: { top: number; right: number; left: number; bottom: number };
  showLegend: boolean;
  showGrid: boolean;
  yAxisWidth: number;
}

/**
 * Determine size category based on component dimensions
 */
export function getChartSizeConfig(
  width?: number,
  height?: number
): ChartSizeConfig {
  const containerWidth = width || 400;
  const containerHeight = height || 300;
  const isVerySmall = containerWidth < 120 || containerHeight < 100;
  const isSmall = containerWidth < 200 || containerHeight < 150;
  const isMedium = containerWidth < 400 || containerHeight < 250;

  return {
    isVerySmall,
    isSmall,
    isMedium,
    containerWidth,
    containerHeight,
  };
}

/**
 * Get responsive configuration for chart rendering
 */
export function getChartResponsiveConfig(
  sizeConfig: ChartSizeConfig,
  hasTitle: boolean,
  hasDescription: boolean,
  userMargin?: { top?: number; right?: number; left?: number; bottom?: number },
  userShowLegend: boolean = true,
  userShowGrid: boolean = true
): ChartResponsiveConfig {
  const { isVerySmall, isSmall, isMedium } = sizeConfig;

  // Adjust chart margins based on size
  const defaultMargin = { top: 20, right: 30, left: 20, bottom: 30 };

  // Check if user provided explicit margin values (including negative margins for edge-to-edge charts)
  const hasExplicitMargins = userMargin &&
    (userMargin.top !== undefined || userMargin.right !== undefined ||
     userMargin.left !== undefined || userMargin.bottom !== undefined);

  const margin = hasExplicitMargins
    ? {
        top: userMargin?.top ?? defaultMargin.top,
        right: userMargin?.right ?? defaultMargin.right,
        left: userMargin?.left ?? defaultMargin.left,
        bottom: userMargin?.bottom ?? defaultMargin.bottom,
      }
    : isVerySmall
    ? { top: 5, right: 5, left: 5, bottom: 15 }
    : isSmall
    ? { top: 10, right: 10, left: 10, bottom: 20 }
    : isMedium
    ? { top: 15, right: 20, left: 15, bottom: 25 }
    : defaultMargin;

  // Cap margin.left for smaller viewports to prevent excessive left spacing
  // (e.g., templates set margin.left=50 which is fine for desktop but too wide for mobile)
  const maxLeftMargin = isVerySmall ? 2 : isSmall ? 2 : isMedium ? 2 : Infinity;
  if (margin.left > maxLeftMargin) {
    margin.left = maxLeftMargin;
  }

  // Determine title styles based on container size
  const titleClass = isVerySmall
    ? "text-[10px] font-medium"
    : isSmall
    ? "text-xs font-semibold"
    : isMedium
    ? "text-sm font-semibold"
    : "text-lg font-semibold";

  const titleMargin = isVerySmall ? "mb-0.5" : isSmall ? "mb-1" : "mb-2";

  // Show/hide elements based on size
  const showTitle = !isVerySmall && hasTitle;
  const showDescription = !isSmall && hasDescription;
  const showLegend = isVerySmall ? false : userShowLegend;
  const showGrid = isVerySmall ? false : userShowGrid;

  // Calculate font sizes for chart elements
  const axisFontSize = isVerySmall ? 6 : isSmall ? 8 : isMedium ? 10 : 12;
  const legendFontSize = isVerySmall ? 6 : isSmall ? 8 : isMedium ? 10 : 12;

  // Responsive Y-axis width to reduce left spacing on smaller viewports
  const yAxisWidth = isVerySmall ? 25 : isSmall ? 30 : isMedium ? 38 : 60;

  return {
    showTitle,
    showDescription,
    titleClass,
    titleMargin,
    axisFontSize,
    legendFontSize,
    margin,
    showLegend,
    showGrid,
    yAxisWidth,
  };
}

// ============================================================================
// Phase 2: Responsive Chart Configuration per Breakpoint
// ============================================================================

export type Breakpoint = 'mobile' | 'tablet' | 'desktop';

/**
 * Phase 2: Breakpoint-specific chart configuration
 * Matches ChartResponsiveConfig from BaseCommandHandler
 */
export interface BreakpointChartConfig {
  // Visibility toggles
  showLegend?: boolean;
  showGrid?: boolean;
  showTooltip?: boolean;
  showTooltipContent?: boolean;
  showXAxis?: boolean;
  showYAxis?: boolean;
  showDots?: boolean;

  // Size-related properties
  strokeWidth?: number;
  dotSize?: number;
  axisFontSize?: number;
  legendFontSize?: number;

  // Layout
  margin?: { top?: number; right?: number; bottom?: number; left?: number };

  // Title
  title?: string | null;

  // Line style properties
  strokeDasharray?: string;
  strokeOpacity?: number;
  lineType?: string;

  // Style overrides
  style?: Record<string, unknown>;
}

export interface ResponsiveChartConfigInput {
  mobile?: BreakpointChartConfig;
  tablet?: BreakpointChartConfig;
  desktop?: BreakpointChartConfig;
}

/**
 * Phase 2: Determine current breakpoint based on container width
 * Breakpoint thresholds aligned with TerraMatrix responsive system:
 * - Mobile: width ≤ 599px
 * - Tablet: 600px ≤ width ≤ 1023px
 * - Desktop: width ≥ 1024px
 */
export function getBreakpoint(containerWidth: number): Breakpoint {
  if (containerWidth < 600) {
    return 'mobile';
  } else if (containerWidth < 1024) {
    return 'tablet';
  }
  return 'desktop';
}

/**
 * Phase 2: Get breakpoint-specific chart configuration
 * Merges base component properties with responsive overrides
 *
 * @param containerWidth - Current container width in pixels
 * @param responsiveConfig - Breakpoint-specific configurations from component.properties
 * @returns The configuration for the current breakpoint, or undefined if no responsive config
 */
export function getBreakpointChartConfig(
  containerWidth: number,
  responsiveConfig?: ResponsiveChartConfigInput
): BreakpointChartConfig | undefined {
  if (!responsiveConfig) {
    return undefined;
  }

  const breakpoint = getBreakpoint(containerWidth);
  const breakpointConfig = responsiveConfig[breakpoint];

  return breakpointConfig;
}

/**
 * Phase 2: Apply breakpoint-specific overrides to chart properties
 * Returns merged properties with breakpoint-specific values taking precedence
 */
export function applyBreakpointOverrides<T extends Record<string, unknown>>(
  baseProperties: T,
  breakpointConfig?: BreakpointChartConfig
): T {
  if (!breakpointConfig) {
    return baseProperties;
  }

  const merged = { ...baseProperties };

  // Apply each defined override
  if (breakpointConfig.showLegend !== undefined) {
    (merged as Record<string, unknown>).showLegend = breakpointConfig.showLegend;
  }
  if (breakpointConfig.showGrid !== undefined) {
    (merged as Record<string, unknown>).showGrid = breakpointConfig.showGrid;
  }
  if (breakpointConfig.showTooltip !== undefined) {
    (merged as Record<string, unknown>).showTooltip = breakpointConfig.showTooltip;
  }
  if (breakpointConfig.showXAxis !== undefined) {
    (merged as Record<string, unknown>).showXAxis = breakpointConfig.showXAxis;
  }
  if (breakpointConfig.showYAxis !== undefined) {
    (merged as Record<string, unknown>).showYAxis = breakpointConfig.showYAxis;
  }
  if (breakpointConfig.showDots !== undefined) {
    (merged as Record<string, unknown>).showDots = breakpointConfig.showDots;
  }
  if (breakpointConfig.strokeWidth !== undefined) {
    (merged as Record<string, unknown>).strokeWidth = breakpointConfig.strokeWidth;
  }
  if (breakpointConfig.dotSize !== undefined) {
    (merged as Record<string, unknown>).dotSize = breakpointConfig.dotSize;
  }
  if (breakpointConfig.axisFontSize !== undefined) {
    (merged as Record<string, unknown>).axisFontSize = breakpointConfig.axisFontSize;
  }
  if (breakpointConfig.legendFontSize !== undefined) {
    (merged as Record<string, unknown>).legendFontSize = breakpointConfig.legendFontSize;
  }
  if (breakpointConfig.margin !== undefined) {
    (merged as Record<string, unknown>).margin = breakpointConfig.margin;
  }
  if (breakpointConfig.title !== undefined) {
    (merged as Record<string, unknown>).title = breakpointConfig.title;
  }
  if (breakpointConfig.strokeDasharray !== undefined) {
    (merged as Record<string, unknown>).strokeDasharray = breakpointConfig.strokeDasharray;
  }
  if (breakpointConfig.strokeOpacity !== undefined) {
    (merged as Record<string, unknown>).strokeOpacity = breakpointConfig.strokeOpacity;
  }
  if (breakpointConfig.lineType !== undefined) {
    (merged as Record<string, unknown>).lineType = breakpointConfig.lineType;
  }

  return merged;
}
